import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { ReactNode } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Trash2, Send, Plus } from "lucide-react";
import {
  listAlertRules,
  upsertAlertRule,
  deleteAlertRule,
  testAlertRule,
} from "@/lib/alert-rules.functions";

export const Route = createFileRoute("/_authenticated/alert-rules")({
  component: AlertRulesPage,
  head: () => ({ meta: [{ title: "Alert Rules — InboxPulse" }] }),
});

type RuleForm = {
  id?: string;
  name: string;
  metric_name: string;
  label_filters_text: string; // freeform key=value,key=value
  window_minutes: number;
  threshold: number;
  cooldown_minutes: number;
  enabled: boolean;
  telegram_chat_id: string;
};

const emptyForm: RuleForm = {
  name: "",
  metric_name: "",
  label_filters_text: "",
  window_minutes: 5,
  threshold: 100,
  cooldown_minutes: 15,
  enabled: true,
  telegram_chat_id: "",
};

function parseLabels(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const part of text.split(",")) {
    const [k, v] = part.split("=").map((s) => s?.trim());
    if (k && v) out[k] = v;
  }
  return out;
}

function labelsToText(obj: Record<string, string> | null | undefined): string {
  if (!obj) return "";
  return Object.entries(obj)
    .map(([k, v]) => `${k}=${v}`)
    .join(", ");
}

function AlertRulesPage() {
  const fetchRules = useServerFn(listAlertRules);
  const saveRule = useServerFn(upsertAlertRule);
  const removeRule = useServerFn(deleteAlertRule);
  const fireTest = useServerFn(testAlertRule);
  const qc = useQueryClient();

  const [form, setForm] = useState<RuleForm>(emptyForm);

  const { data, isLoading } = useQuery({
    queryKey: ["alert-rules"],
    queryFn: () => fetchRules(),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["alert-rules"] });

  const saveMutation = useMutation({
    mutationFn: (f: RuleForm) =>
      saveRule({
        data: {
          id: f.id,
          name: f.name,
          metric_name: f.metric_name,
          label_filters: parseLabels(f.label_filters_text),
          window_minutes: f.window_minutes,
          threshold: f.threshold,
          cooldown_minutes: f.cooldown_minutes,
          enabled: f.enabled,
          telegram_chat_id: f.telegram_chat_id.trim() || null,
        },
      }),
    onSuccess: () => {
      toast.success("Rule saved");
      setForm(emptyForm);
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => removeRule({ data: { id } }),
    onSuccess: () => {
      toast.success("Rule deleted");
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const testMutation = useMutation({
    mutationFn: (id: string) => fireTest({ data: { id } }),
    onSuccess: (res) => {
      toast.success(`Test alert dispatched (telegram=${res.channels.telegram})`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="p-8 max-w-5xl space-y-6">
      <PageHeader
        title="Alert Rules"
        description="On-call alerts when metric counters spike. Routes exclusively via Telegram."
      />

      <HelpPanel
        title="Hilfe: Alarm-Regeln einrichten"
        what={
          <>
            Hier sagst du dem System: <b>WANN soll es Alarm schlagen?</b> Beispiel: "Wenn
            Bounce-Rate über 5% steigt, schick mir Telegram-Nachricht".
          </>
        }
        why={
          <>
            Du kannst nicht 24/7 ins Dashboard schauen. Mit Regeln wirst du sofort gewarnt, wenn
            etwas Wichtiges schiefläuft.
          </>
        }
        how={[
          <>
            <b>Neue Regel</b> anlegen.
          </>,
          <>Auslöser wählen (z.B. "Bounce-Rate", "Spam-Rate", "Fehler/h").</>,
          <>Schwelle setzen (z.B. "über 5%").</>,
          <>Empfänger eintragen (Telegram-Chat-ID).</>,
          <>Speichern — fertig. Bei Auslöser kommt die Nachricht innerhalb von 1-2 Minuten.</>,
        ]}
        tips={[
          <>Lieber wenige scharfe Regeln als 50 ungenaue — sonst stumpfst du ab.</>,
          <>Test-Regel mit niedriger Schwelle anlegen, um die Telegram-Verbindung zu prüfen.</>,
        ]}
        glossary={[
          { term: "Schwelle", def: "Grenze, ab der die Regel auslöst (z.B. 5%, 100 Fehler)." },
          {
            term: "Telegram-Chat-ID",
            def: "Eindeutige Nummer deines Telegram-Chats. Findest du über den BotFather.",
          },
        ]}
      />

      <Card className="p-6 bg-card/60 border-border space-y-4">
        <div className="flex items-center gap-2">
          <Plus className="size-4 text-muted-foreground" />
          <h2 className="font-semibold">{form.id ? "Edit rule" : "New rule"}</h2>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Name">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <Field label="Metric counter name" hint="e.g. pixel_rate_limited_total">
            <Input
              value={form.metric_name}
              onChange={(e) => setForm({ ...form, metric_name: e.target.value })}
            />
          </Field>
          <Field
            label="Label filters"
            hint="key=value, key=value (subset match). Empty = all labels."
          >
            <Input
              placeholder="provider=Gmail, device=desktop"
              value={form.label_filters_text}
              onChange={(e) => setForm({ ...form, label_filters_text: e.target.value })}
            />
          </Field>
          <Field label="Window (minutes)" hint="Spike window between evaluations.">
            <Input
              type="number"
              min={1}
              max={1440}
              value={form.window_minutes}
              onChange={(e) =>
                setForm({ ...form, window_minutes: Number.parseInt(e.target.value) || 1 })
              }
            />
          </Field>
          <Field
            label="Threshold (delta)"
            hint="Fire when counter increased by ≥ this in the window."
          >
            <Input
              type="number"
              min={1}
              value={form.threshold}
              onChange={(e) =>
                setForm({ ...form, threshold: Number.parseInt(e.target.value) || 1 })
              }
            />
          </Field>
          <Field label="Cooldown (minutes)" hint="Min gap between consecutive fires for this rule.">
            <Input
              type="number"
              min={0}
              max={10080}
              value={form.cooldown_minutes}
              onChange={(e) =>
                setForm({ ...form, cooldown_minutes: Number.parseInt(e.target.value) || 0 })
              }
            />
          </Field>
          <Field
            label="Telegram chat ID"
            hint="Numeric chat ID. Leave empty to use the global Telegram chat from alert settings."
          >
            <Input
              value={form.telegram_chat_id}
              onChange={(e) => setForm({ ...form, telegram_chat_id: e.target.value })}
            />
          </Field>
        </div>
        <div className="flex items-center justify-between pt-2">
          <div className="flex items-center gap-2">
            <Switch
              checked={form.enabled}
              onCheckedChange={(v) => setForm({ ...form, enabled: v })}
            />
            <span className="text-sm text-muted-foreground">Enabled</span>
          </div>
          <div className="flex gap-2">
            {form.id && (
              <Button variant="outline" onClick={() => setForm(emptyForm)}>
                Cancel
              </Button>
            )}
            <Button
              onClick={() => saveMutation.mutate(form)}
              disabled={saveMutation.isPending || !form.name || !form.metric_name}
              className="bg-[image:var(--gradient-primary)] hover:opacity-90"
            >
              {saveMutation.isPending ? "Saving…" : form.id ? "Update rule" : "Create rule"}
            </Button>
          </div>
        </div>
      </Card>

      <Card className="p-6 bg-card/60 border-border">
        <h2 className="font-semibold mb-4">Configured rules</h2>
        {isLoading && <p className="text-muted-foreground text-sm">Loading…</p>}
        {!isLoading && (data?.length ?? 0) === 0 && (
          <p className="text-muted-foreground text-sm">No rules yet. Create one above.</p>
        )}
        <div className="space-y-3">
          {(data ?? []).map((rule) => (
            <div
              key={rule.id}
              className="flex items-start justify-between gap-4 rounded-lg border border-border bg-background/40 p-4"
            >
              <div className="space-y-1 text-sm">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{rule.name}</span>
                  {!rule.enabled && <Badge variant="secondary">disabled</Badge>}
                  {rule.telegram_chat_id && <Badge variant="outline">telegram</Badge>}
                </div>
                <div className="text-muted-foreground font-mono text-xs">
                  {rule.metric_name} • Δ≥{rule.threshold} in {rule.window_minutes}m • cooldown{" "}
                  {rule.cooldown_minutes}m
                </div>
                <div className="text-muted-foreground text-xs">
                  Labels: {labelsToText(rule.label_filters) || "—"}
                </div>
                <div className="text-muted-foreground text-xs">
                  Last value: {rule.last_value} • Last fired:{" "}
                  {rule.last_fired_at ? new Date(rule.last_fired_at).toLocaleString() : "never"}
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    setForm({
                      id: rule.id,
                      name: rule.name,
                      metric_name: rule.metric_name,
                      label_filters_text: labelsToText(rule.label_filters),
                      window_minutes: rule.window_minutes,
                      threshold: Number(rule.threshold),
                      cooldown_minutes: rule.cooldown_minutes,
                      enabled: rule.enabled,
                      telegram_chat_id: rule.telegram_chat_id ?? "",
                    })
                  }
                >
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => testMutation.mutate(rule.id)}
                  disabled={testMutation.isPending}
                >
                  <Send className="size-3.5 mr-1" /> Test
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive hover:text-destructive"
                  onClick={() => {
                    if (confirm(`Delete rule "${rule.name}"?`)) deleteMutation.mutate(rule.id);
                  }}
                >
                  <Trash2 className="size-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}
